import { useMutation } from "@apollo/client/react";
import { useNavigate } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import { Trash } from "components/icons/generated";
import ButtonIcon from "ds/components/ButtonIcon";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import { Session } from "types/generated";
import { AccountContext } from "views/AccountWrapper";

import { showSessionsConfirmationModal } from "../../ConfirmationModal";
import { SESSION_DELETE } from "../../gql";

const SessionsTableActionsCell = ({
  item: session,
  ...cellProps
}: TableSimpleCellProps<Pick<Session, "id" | "login" | "current">>) => {
  const navigate = useNavigate();

  const { viewer } = useTypedContext(AccountContext);
  const { reportSuccess, onError } = useTypedContext(FlashContext);

  const [sessionDelete, { loading }] = useMutation(SESSION_DELETE, {
    variables: { id: session.id },
    refetchQueries: ["GetSessions"],
  });

  const handleDeactivation = () => {
    const asyncFn = async () => {
      if (session.current) {
        return navigate("/logout");
      }

      try {
        await sessionDelete();
        reportSuccess({ message: `Session ${session.id} was deactivated` });
      } catch (error) {
        onError(error);
      }
    };

    void asyncFn();
  };

  const handleDeactivationConfirmation = () =>
    showSessionsConfirmationModal({
      confirmationCallback: handleDeactivation,
      sessionId: session.id,
      userId: session.login,
      isCurrentSession: session.current,
    });

  return (
    <TableSimpleCell {...cellProps}>
      {viewer.admin && (
        <ButtonIcon
          onPress={handleDeactivationConfirmation}
          icon={Trash}
          variant="ghostDanger"
          tooltipPlacement="left"
          disabled={loading}
        >
          {session.current ? "Log out of your session" : `Deactivate session ${session.id}`}
        </ButtonIcon>
      )}
    </TableSimpleCell>
  );
};

export default SessionsTableActionsCell;
